import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, getDoc, updateDoc, deleteDoc } from 'firebase/firestore';
import { db } from '../utils/firebase';
import { makeUserAdmin, deleteUserDocument } from '../utils/makeAdmin';
import { changeUserRoleByEmail } from '../utils/changeUserRole';
import { generateHostId } from '../utils/hostAchievements';
import { adminResetPassword } from '../utils/passwordReset';
import { 
  getAllBadges, 
  getCurrentBadges, 
  awardBadge, 
  removeBadge 
} from '../utils/badgeManagement';
import { 
  manuallyVerifyFirebaseAuthEmail, 
  resendVerificationEmail 
} from '../utils/emailVerification';
import { 
  grantFreeLaunch, 
  grantFreeGameAccess, 
  grantCreatorAccess, 
  getUserAccess 
} from '../utils/adminGrantUtils';
import Header from '../components/Header';

const AdminUserDetail = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [currentBadges, setCurrentBadges] = useState([]);
  const [availableBadges, setAvailableBadges] = useState([]);
  const [selectedBadge, setSelectedBadge] = useState('');
  const [badgeLoading, setBadgeLoading] = useState(false);
  
  // Grant access state
  const [games, setGames] = useState([]);
  const [filteredGames, setFilteredGames] = useState([]);
  const [userAccess, setUserAccess] = useState(null);
  const [selectedGame, setSelectedGame] = useState('');
  const [grantType, setGrantType] = useState('launch');
  const [reason, setReason] = useState('');
  const [durationHours, setDurationHours] = useState(24);
  const [grantMessage, setGrantMessage] = useState('');
  const [grantLoading, setGrantLoading] = useState(false);
  
  // Subscription management state
  const [selectedCreatorRole, setSelectedCreatorRole] = useState('');
  const [selectedDuration, setSelectedDuration] = useState('');

  const fetchUserData = useCallback(async () => {
    try {
      setLoading(true);
      const userDoc = await getDoc(doc(db, 'users', userId));
      if (userDoc.exists()) {
        setUser({ id: userDoc.id, ...userDoc.data() });
      } else {
        setError('User not found');
      }
    } catch (err) {
      console.error('Error fetching user:', err);
      setError('Failed to load user data');
    } finally {
      setLoading(false);
    }
  }, [userId]);

  const loadBadges = useCallback(async () => {
    if (!user) return;
    
    try {
      // Determine user type for badge management
      let userType = 'host'; // default
      if (user.role === 'team_captain' || user.role === 'team_member') {
        userType = 'team';
      } else if (user.role === 'venue') {
        userType = 'venue';
      }

      // Load current badges
      const badges = await getCurrentBadges(user.id, userType);
      setCurrentBadges(badges);

      // Load available badges
      const { allBadges } = getAllBadges(userType);
      setAvailableBadges(allBadges);
    } catch (error) {
      console.error('Error loading badges:', error);
    }
  }, [user]);

  useEffect(() => {
    fetchUserData();
  }, [userId, fetchUserData]);

  useEffect(() => {
    if (user) {
      loadBadges();
      loadGames();
      loadUserAccess();
    }
  }, [user, loadBadges]);

  // Load available games for grant access
  const loadGames = useCallback(async () => {
    try {
      const { collection, query, getDocs, where, limit } = await import('firebase/firestore');
      
      // Query for marketplace games (approved/public games in the correct collection)
      const marketplaceQuery = query(
        collection(db, 'games', 'content', 'triviaGames'),
        where('status', 'in', ['approved', 'public']),
        limit(50)
      );
      
      const marketplaceSnapshot = await getDocs(marketplaceQuery);
      const gamesList = [];
      
      marketplaceSnapshot.forEach(doc => {
        gamesList.push({
          id: doc.id,
          ...doc.data()
        });
      });
      
      // If no marketplace games, try to get all games from the same collection
      if (gamesList.length === 0) {
        console.log('No marketplace games found, trying all games from triviaGames collection...');
        const allGamesQuery = query(
          collection(db, 'games', 'content', 'triviaGames'),
          limit(50)
        );
        
        const allGamesSnapshot = await getDocs(allGamesQuery);
        allGamesSnapshot.forEach(doc => {
          const gameData = doc.data();
          gamesList.push({
            id: doc.id,
            ...gameData
          });
        });
      }
      
      // Sort games by title on the client side
      gamesList.sort((a, b) => (a.title || '').localeCompare(b.title || ''));
      
      console.log(`Loaded ${gamesList.length} games for grant access`);
      setGames(gamesList);
      
      if (gamesList.length === 0) {
        setGrantMessage('⚠️ No games found in database. Please create some games first.');
      }
    } catch (error) {
      console.error('Error loading games:', error);
      setGrantMessage(`Error loading games: ${error.message}`);
    }
  }, []);

  // Load user's current access
  const loadUserAccess = useCallback(async () => {
    try {
      const access = await getUserAccess(userId);
      setUserAccess(access);
    } catch (error) {
      console.error('Error loading user access:', error);
      setGrantMessage('Error loading user access');
    }
  }, [userId]);

  const handleAwardBadge = async () => {
    if (!selectedBadge) {
      alert('Please select a badge to award');
      return;
    }

    if (!window.confirm(`Are you sure you want to award the "${selectedBadge}" badge to this user?`)) {
      return;
    }

    try {
      setBadgeLoading(true);
      let userType = 'host';
      if (user.role === 'team_captain' || user.role === 'team_member') {
        userType = 'team';
      } else if (user.role === 'venue') {
        userType = 'venue';
      }

      const result = await awardBadge(user.id, userType, selectedBadge, 'admin');
      
      if (result.success) {
        alert(`✅ ${result.message}`);
        await loadBadges();
        setSelectedBadge('');
      } else {
        alert(`❌ ${result.message}`);
      }
    } catch (error) {
      alert(`❌ Error awarding badge: ${error.message}`);
    } finally {
      setBadgeLoading(false);
    }
  };

  const handleRemoveBadge = async (badgeId) => {
    if (!window.confirm(`Are you sure you want to remove the "${badgeId}" badge from this user?`)) {
      return;
    }

    try {
      setBadgeLoading(true);
      let userType = 'host';
      if (user.role === 'team_captain' || user.role === 'team_member') {
        userType = 'team';
      } else if (user.role === 'venue') {
        userType = 'venue';
      }

      const result = await removeBadge(user.id, userType, badgeId, 'admin');
      
      if (result.success) {
        alert(`✅ ${result.message}`);
        await loadBadges();
      } else {
        alert(`❌ ${result.message}`);
      }
    } catch (error) {
      alert(`❌ Error removing badge: ${error.message}`);
    } finally {
      setBadgeLoading(false);
    }
  };

  const handleUpdateUser = async (updates) => {
    try {
      setSaving(true);
      const userRef = doc(db, 'users', userId);
      await updateDoc(userRef, updates);
      await fetchUserData(); // Refresh data
      alert('✅ User updated successfully!');
    } catch (err) {
      console.error('Error updating user:', err);
      setError('Failed to update user');
    } finally {
      setSaving(false);
    }
  };

    // Removed unused handleCreditOperation function

  const handleChangeRole = async (newRole) => {
    const roleNames = {
      'host': 'Host',
      'team_captain': 'Team Captain',
      'team_member': 'Team Member',
      'venue': 'Venue',
      'admin': 'Admin'
    };

    if (!window.confirm(`⚠️ Are you sure you want to change this user's role to ${roleNames[newRole]}?\n\nThis will modify their access permissions and capabilities.`)) {
      return;
    }

    try {
      setSaving(true);
      const success = await changeUserRoleByEmail(user.email, newRole);
      if (success) {
        await fetchUserData();
        alert(`✅ User role changed to ${roleNames[newRole]} successfully!`);
      } else {
        alert('❌ Failed to change role. User might not exist in Firestore.');
      }
    } catch (err) {
      setError('Failed to change user role');
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  const handleMakeAdmin = async () => {
    if (!window.confirm('⚠️ Are you sure you want to make this user an admin?\n\nThis will give them full access to the admin panel and all system functions. This action cannot be easily undone.')) {
      return;
    }

    try {
      setSaving(true);
      await makeUserAdmin(userId);
      await fetchUserData();
      alert('✅ User made admin successfully!');
    } catch (err) {
      setError('Failed to make user admin');
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteUser = async () => {
    if (!window.confirm('⚠️ Are you sure you want to delete this user?\n\nThis will mark the user as deleted and remove their access to the system. Their data will be preserved for audit purposes but they will no longer be able to sign in.\n\nThis action cannot be easily undone.')) {
      return;
    }

    try {
      setSaving(true);
      await deleteUserDocument(userId);
      alert('✅ User deleted successfully!');
      navigate('/admin/users');
    } catch (err) {
      setError('Failed to delete user');
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  const handlePermanentlyDelete = async () => {
    if (!window.confirm('🚨 DANGER: Are you absolutely sure you want to PERMANENTLY DELETE this user?\n\nThis will completely remove all user data from the database. This action is IRREVERSIBLE and will delete:\n• User profile\n• All associated data\n• Game history\n• Team memberships\n• All other user-related information\n\nType "DELETE" to confirm:')) {
      return;
    }

    const confirmation = prompt('Type "DELETE" to confirm permanent deletion:');
    if (confirmation !== 'DELETE') {
      alert('Deletion cancelled.');
      return;
    }

    try {
      setSaving(true);
      await deleteDoc(doc(db, 'users', userId));
      alert('✅ User permanently deleted!');
      navigate('/admin/users');
    } catch (err) {
      setError('Failed to permanently delete user');
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  const handleAssignCreatorRole = async () => {
    if (!window.confirm(`⚠️ Are you sure you want to assign ${selectedCreatorRole} role to this user for ${selectedDuration}?\n\nThis will override their current subscription status.`)) {
      return;
    }

    try {
      setSaving(true);
      
      // Calculate expiration date based on duration
      let expirationDate = null;
      const now = new Date();
      
      switch (selectedDuration) {
        case '1_week':
          expirationDate = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000);
          break;
        case '1_month':
          expirationDate = new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000);
          break;
        case '3_months':
          expirationDate = new Date(now.getTime() + 90 * 24 * 60 * 60 * 1000);
          break;
        case '6_months':
          expirationDate = new Date(now.getTime() + 180 * 24 * 60 * 60 * 1000);
          break;
        case '1_year':
          expirationDate = new Date(now.getTime() + 365 * 24 * 60 * 60 * 1000);
          break;
        case 'permanent':
          expirationDate = null; // No expiration
          break;
        default:
          throw new Error('Invalid duration selected');
      }

      // Determine product ID based on role
      let productId = null;
      if (selectedCreatorRole === 'creator_lite') {
        productId = 'prod_Ssvv7A01Jdt4uB';
      } else if (selectedCreatorRole === 'creator_pro') {
        productId = 'prod_SsvxGWP8OXQ96P';
      }

      // Update user document
      const userRef = doc(db, 'users', userId);
      const updates = {
        role: selectedCreatorRole,
        subscription: {
          status: 'active',
          productId: productId,
          currentPeriodEnd: expirationDate,
          adminOverride: true,
          adminOverrideExpiration: expirationDate,
          adminOverrideReason: `Manual assignment by admin for ${selectedDuration}`
        }
      };

      await updateDoc(userRef, updates);
      await fetchUserData();
      alert(`✅ ${selectedCreatorRole} role assigned successfully for ${selectedDuration}!`);
      
      // Reset form
      setSelectedCreatorRole('');
      setSelectedDuration('');
      
    } catch (err) {
      console.error('Error assigning creator role:', err);
      setError('Failed to assign creator role');
    } finally {
      setSaving(false);
    }
  };

  const handleResetEntitlements = async () => {
    if (!window.confirm('⚠️ Are you sure you want to reset this user\'s monthly entitlements?\n\nThis will reset their build and submission counts to 0.')) {
      return;
    }

    try {
      setSaving(true);
      
      const userRef = doc(db, 'users', userId);
      const updates = {
        creatorEntitlements: {
          buildsThisPeriod: 0,
          submitsThisPeriod: 0,
          periodEnd: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000), // Reset to 30 days from now
          lastReset: new Date()
        }
      };

      await updateDoc(userRef, updates);
      await fetchUserData();
      alert('✅ Entitlements reset successfully!');
      
    } catch (err) {
      console.error('Error resetting entitlements:', err);
      setError('Failed to reset entitlements');
    } finally {
      setSaving(false);
    }
  };

  const handleAssignHostId = async () => {
    if (!window.confirm('⚠️ This will assign a sequential Host ID to this user.\n\nThis action cannot be undone. Continue?')) {
      return;
    }

    try {
      setSaving(true);
      const newHostId = await generateHostId(db);
      
      await updateDoc(doc(db, 'users', userId), {
        hostId: newHostId
      });
      
      await fetchUserData(); // Refresh the data
      alert(`✅ Host ID #${newHostId} assigned successfully!`);
    } catch (err) {
      setError('Failed to assign host ID');
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  const handleSetCustomId = async () => {
    const isHost = user.role === 'host';
    const isTeam = user.role === 'team_captain' || user.role === 'team_member';
    
    if (!isHost && !isTeam) {
      alert('❌ Custom ID can only be set for hosts and teams.');
      return;
    }

    const idType = isHost ? 'Host ID' : 'League ID';
    const currentId = isHost ? user.hostId : user.leagueId;
    
    const customId = prompt(
      `Enter custom ${idType} number for ${user.fullName || user.email}:\n\n` +
      `Current ${idType}: ${currentId || 'Not assigned'}\n\n` +
      `Enter a number (e.g., 42, 100, 777):`,
      currentId || ''
    );

    if (!customId || customId.trim() === '') {
      return; // User cancelled
    }

    const numericId = customId.trim();
    
    // Validate that it's a positive number
    if (!/^\d+$/.test(numericId) || parseInt(numericId) <= 0) {
      alert('❌ Please enter a valid positive number (e.g., 42, 100, 777)');
      return;
    }

    if (!window.confirm(
      `⚠️ Are you sure you want to set ${idType} to #${numericId}?\n\n` +
      `This will override the current ${idType} and cannot be easily undone.\n\n` +
      `User: ${user.fullName || user.email}\n` +
      `New ${idType}: #${numericId}`
    )) {
      return;
    }

    try {
      setSaving(true);
      
      const updateData = {};
      if (isHost) {
        updateData.hostId = numericId;
      } else if (isTeam) {
        updateData.leagueId = numericId;
      }
      
      await updateDoc(doc(db, 'users', userId), updateData);
      
      await fetchUserData(); // Refresh the data
      alert(`✅ ${idType} set to #${numericId} successfully!`);
    } catch (err) {
      setError(`Failed to set ${idType}`);
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  const handleResetPassword = async () => {
    if (!window.confirm('⚠️ Are you sure you want to reset this user\'s password?\n\nThis will send a password reset email to the user.')) {
      return;
    }

    try {
      setSaving(true);
      const result = await adminResetPassword(user.uid, user.email);
      if (result.success) {
        alert(`✅ ${result.message}\n\nTemporary password: ${result.tempPassword}\n\nPlease share this temporary password with the user securely.`);
      } else {
        alert(`❌ ${result.message}`);
      }
    } catch (err) {
      setError('Failed to send password reset email');
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  // Removed unused handleVerifyFirebaseAuth function

  const handleResendVerificationEmail = async () => {
    if (!window.confirm('⚠️ Are you sure you want to resend the verification email to this user?\n\nThis will send a new verification email to the user\'s email address.')) {
      return;
    }

    try {
      setSaving(true);
      const result = await resendVerificationEmail(user.id);
      if (result.success) {
        alert('✅ Verification email sent successfully! Check the user\'s inbox.');
      } else {
        alert(`❌ Error: ${result.message}`);
      }
    } catch (error) {
      alert(`❌ Error resending verification email: ${error.message}`);
    } finally {
      setSaving(false);
    }
  };

  // Handle grant access
  const handleGrantAccess = async () => {
    if (!selectedGame && grantType !== 'creator') {
      setGrantMessage('Please select a game');
      return;
    }

    setGrantLoading(true);
    setGrantMessage('');

    try {
      const game = games.find(g => g.id === selectedGame);
      const gameTitle = game?.title || 'Unknown Game';

      let result;
      switch (grantType) {
        case 'launch':
          result = await grantFreeLaunch(userId, selectedGame, gameTitle, reason);
          break;
        case 'game':
          result = await grantFreeGameAccess(userId, selectedGame, gameTitle, reason);
          break;
        case 'creator':
          result = await grantCreatorAccess(userId, durationHours, reason);
          break;
        default:
          throw new Error('Invalid grant type');
      }

      setGrantMessage(`✅ ${result.message}`);
      
      // Refresh user access
      await loadUserAccess();
      
      // Reset form
      setSelectedGame('');
      setReason('');
    } catch (error) {
      console.error('Error granting access:', error);
      setGrantMessage(`❌ Error: ${error.message}`);
    } finally {
      setGrantLoading(false);
    }
  };

  const getRoleBadge = (role) => {
    const roleConfig = {
      'admin': { bg: 'bg-purple-100 text-purple-800', icon: '👑' },
      'team_captain': { bg: 'bg-blue-100 text-blue-800', icon: '🏆' },
      'team_member': { bg: 'bg-green-100 text-green-800', icon: '👥' },
      'venue': { bg: 'bg-orange-100 text-orange-800', icon: '🏢' },
      'host': { bg: 'bg-gray-100 text-gray-800', icon: '🎤' }
    };

    const config = roleConfig[role] || roleConfig['host'];
    return (
      <span className={`px-3 py-1 rounded-full text-sm font-medium ${config.bg}`}>
        {config.icon} {role || 'host'}
      </span>
    );
  };

  const getTabConfig = () => [
    { id: 'overview', name: 'Overview', icon: '👤' },
    { id: 'profile', name: 'Profile', icon: '📝' },
    { id: 'permissions', name: 'Permissions', icon: '🔐' },
    { id: 'activity', name: 'Activity', icon: '📊' },
    { id: 'danger', name: 'Danger Zone', icon: '⚠️' }
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-400 mx-auto"></div>
          <p className="mt-4 text-gray-300">Loading user data...</p>
        </div>
      </div>
    );
  }

  if (error || !user) {
    return (
      <div className="min-h-screen bg-gray-900">
        <Header />
        <div className="p-8">
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
            {error || 'User not found'}
          </div>
          <button
            onClick={() => navigate('/admin/users')}
            className="mt-4 bg-gray-800 text-green-300 px-4 py-2 rounded hover:bg-gray-700"
          >
            ← Back to Users
          </button>
        </div>
      </div>
    );
  }

  // Helper to determine if this user is a team (team_captain or team_member)
  const isTeam = user.role === 'team_captain' || user.role === 'team_member';

  return (
    <div className="min-h-screen bg-gray-900">
      <Header />
      <div className="p-8">
        {/* Header */}
        <div className="mb-6">
          <button
            onClick={() => navigate('/admin/users')}
            className="bg-gray-800 text-green-300 px-4 py-2 rounded hover:bg-gray-700 mb-4"
          >
            ← Back to Users
          </button>
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 bg-green-600 rounded-full flex items-center justify-center text-white text-2xl font-bold">
              {user.fullName?.charAt(0)?.toUpperCase() || user.email?.charAt(0)?.toUpperCase() || 'U'}
            </div>
            <div>
              <h1 className="text-3xl font-bold text-white">{user.fullName || 'Unnamed User'}</h1>
              <p className="text-gray-300">{user.email}</p>
              <div className="mt-2">{getRoleBadge(user.role)}</div>
            </div>
          </div>
        </div>

        {/* Error Display */}
        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
            {error}
          </div>
        )}

        {/* Tabs */}
        <div className="bg-gray-800 rounded-lg shadow-md mb-6">
          <div className="flex border-b border-gray-700">
            {getTabConfig().map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex-1 px-4 py-3 text-sm font-medium transition-colors ${
                  activeTab === tab.id
                    ? 'bg-green-600 text-white border-b-2 border-green-400'
                    : 'text-gray-300 hover:text-white hover:bg-gray-700'
                }`}
              >
                <div className="flex items-center justify-center gap-2">
                  <span>{tab.icon}</span>
                  <span>{tab.name}</span>
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Tab Content */}
        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          {activeTab === 'overview' && (
            <div className="p-6">
              <h2 className="text-xl font-semibold mb-4">👤 User Overview</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">User ID</label>
                    <code className="bg-gray-100 px-2 py-1 rounded text-sm">{user.id}</code>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                    <p className="text-gray-900">{user.email}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Full Name</label>
                    <p className="text-gray-900">{user.fullName || 'Not provided'}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Role</label>
                    <div>{getRoleBadge(user.role)}</div>
                  </div>
                  {/* Show League ID for team users */}
                  {isTeam && user.leagueId && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">League ID</label>
                      <div className="flex items-center gap-2">
                        <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded text-sm font-mono">
                          #{user.leagueId}
                        </span>
                        <span className="text-xs text-gray-500">
                          {user.leagueId === '1' ? '🏆 First Team Ever!' : 
                           user.leagueId <= '10' ? '🥇 Early Adopter' :
                           user.leagueId <= '50' ? '🥈 Founding Member' :
                           user.leagueId <= '100' ? '🥉 Early Member' : 'Member'}
                        </span>
                        <button
                          onClick={handleSetCustomId}
                          disabled={saving}
                          className="bg-purple-600 text-white px-2 py-1 rounded text-xs hover:bg-purple-700 disabled:opacity-50"
                          title="Set Custom League ID"
                        >
                          ✏️
                        </button>
                      </div>
                    </div>
                  )}
                  {/* Show League ID assignment for team users without league ID */}
                  {isTeam && !user.leagueId && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">League ID</label>
                      <div className="flex items-center gap-2">
                        <span className="text-gray-500 text-sm">Not assigned</span>
                        <button
                          onClick={handleSetCustomId}
                          disabled={saving}
                          className="bg-blue-600 text-white px-3 py-1 rounded text-sm hover:bg-blue-700 disabled:opacity-50"
                        >
                          {saving ? '🔄 Setting...' : '🏆 Set League ID'}
                        </button>
                      </div>
                    </div>
                  )}
                  {/* Show Host ID for host users */}
                  {user.role === 'host' && user.hostId && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Host ID</label>
                      <div className="flex items-center gap-2">
                        <span className="bg-green-100 text-green-800 px-2 py-1 rounded text-sm font-mono">
                          #{user.hostId}
                        </span>
                        <span className="text-xs text-gray-500">
                          {user.hostId === '1' ? '🎤 First Host Ever!' : 
                           user.hostId <= '10' ? '🥇 Early Adopter' :
                           user.hostId <= '50' ? '🥈 Founding Host' :
                           user.hostId <= '100' ? '🥉 Early Host' : 'Host'}
                        </span>
                        <button
                          onClick={handleSetCustomId}
                          disabled={saving}
                          className="bg-purple-600 text-white px-2 py-1 rounded text-xs hover:bg-purple-700 disabled:opacity-50"
                          title="Set Custom Host ID"
                        >
                          ✏️
                        </button>
                      </div>
                    </div>
                  )}
                  {/* Show Assign Host ID button for host users without host ID */}
                  {user.role === 'host' && !user.hostId && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Host ID</label>
                      <div className="flex items-center gap-2">
                        <span className="text-gray-500 text-sm">Not assigned</span>
                        <button
                          onClick={handleAssignHostId}
                          disabled={saving}
                          className="bg-green-600 text-white px-3 py-1 rounded text-sm hover:bg-green-700 disabled:opacity-50"
                        >
                          {saving ? '🔄 Assigning...' : '🎤 Assign Host ID'}
                        </button>
                        <button
                          onClick={handleSetCustomId}
                          disabled={saving}
                          className="bg-purple-600 text-white px-3 py-1 rounded text-sm hover:bg-purple-700 disabled:opacity-50"
                        >
                          {saving ? '🔄 Setting...' : '✏️ Set Custom ID'}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Created At</label>
                    <p className="text-gray-900">
                      {user.createdAt?.toDate?.().toLocaleString?.() || 'Unknown'}
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Last Active</label>
                    <p className="text-gray-900">
                      {user.lastActive?.toDate?.().toLocaleString?.() || 'Unknown'}
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                    <span className={`px-2 py-1 rounded text-xs ${
                      user.deleted ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800'
                    }`}>
                      {user.deleted ? 'Deleted' : 'Active'}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'profile' && (
            <div className="p-6">
              <h2 className="text-xl font-semibold mb-4">�� Edit Profile</h2>
              
              {/* League Information for Team Users */}
              {isTeam && user.leagueId && (
                <div className="mb-6 p-4 bg-gradient-to-r from-blue-50 to-purple-50 border border-blue-200 rounded-lg">
                  <h3 className="text-lg font-semibold text-blue-800 mb-3">🏆 League Information</h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="text-center">
                      <div className="text-2xl font-bold text-blue-600 font-mono">#{user.leagueId}</div>
                      <div className="text-sm text-gray-600">League ID</div>
                    </div>
                    <div className="text-center">
                      <div className="text-lg font-semibold text-purple-600">
                        {user.leagueId === '1' ? '🏆 First Team Ever!' : 
                         user.leagueId <= '10' ? '🥇 Early Adopter' :
                         user.leagueId <= '50' ? '🥈 Founding Member' :
                         user.leagueId <= '100' ? '🥉 Early Member' : 'Member'}
                      </div>
                      <div className="text-sm text-gray-600">League Status</div>
                    </div>
                    <div className="text-center">
                      <div className="text-lg font-semibold text-green-600">
                        {user.leagueId <= '10' ? 'Exclusive' :
                         user.leagueId <= '50' ? 'Premium' :
                         user.leagueId <= '100' ? 'Early' : 'Standard'}
                      </div>
                      <div className="text-sm text-gray-600">Tier</div>
                    </div>
                  </div>
                  <div className="mt-3 text-xs text-gray-500 text-center">
                    {user.leagueId === '1' ? 'The very first team to join the platform! 🎉' :
                     user.leagueId <= '10' ? 'One of the first 10 teams - exclusive early adopter status!' :
                     user.leagueId <= '50' ? 'Founding member - joined during the early days!' :
                     user.leagueId <= '100' ? 'Early member - joined in the first 100 teams!' :
                     'Member of the trivia community!'}
                  </div>
                </div>
              )}
              
              {/* Host Information for Host Users */}
              {user.role === 'host' && user.hostId && (
                <div className="mb-6 p-4 bg-gradient-to-r from-green-50 to-blue-50 border border-green-200 rounded-lg">
                  <h3 className="text-lg font-semibold text-green-800 mb-3">🎤 Host Information</h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="text-center">
                      <div className="text-2xl font-bold text-green-600 font-mono">#{user.hostId}</div>
                      <div className="text-sm text-gray-600">Host ID</div>
                    </div>
                    <div className="text-center">
                      <div className="text-lg font-semibold text-blue-600">
                        {user.hostId === '1' ? '🎤 First Host Ever!' : 
                         user.hostId <= '10' ? '🥇 Early Adopter' :
                         user.hostId <= '50' ? '🥈 Founding Host' :
                         user.hostId <= '100' ? '🥉 Early Host' : 'Host'}
                      </div>
                      <div className="text-sm text-gray-600">Host Status</div>
                    </div>
                    <div className="text-center">
                      <div className="text-lg font-semibold text-purple-600">
                        {user.hostId <= '10' ? 'Exclusive' :
                         user.hostId <= '50' ? 'Premium' :
                         user.hostId <= '100' ? 'Early' : 'Standard'}
                      </div>
                      <div className="text-sm text-gray-600">Tier</div>
                    </div>
                  </div>
                  <div className="mt-3 text-xs text-gray-500 text-center">
                    {user.hostId === '1' ? 'The very first host to join the platform! 🎉' :
                     user.hostId <= '10' ? 'One of the first 10 hosts - exclusive early adopter status!' :
                     user.hostId <= '50' ? 'Founding host - joined during the early days!' :
                     user.hostId <= '100' ? 'Early host - joined in the first 100 hosts!' :
                     'Member of the hosting community!'}
                  </div>
                </div>
              )}
              
              <form onSubmit={(e) => {
                e.preventDefault();
                const formData = new FormData(e.target);
                handleUpdateUser({
                  fullName: formData.get('fullName'),
                  phone: formData.get('phone'),
                  bio: formData.get('bio')
                });
              }}>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Full Name</label>
                    <input
                      type="text"
                      name="fullName"
                      defaultValue={user.fullName || ''}
                      className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Phone Number</label>
                    <input
                      type="tel"
                      name="phone"
                      defaultValue={user.phone || ''}
                      className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Bio</label>
                    <textarea
                      name="bio"
                      rows="3"
                      defaultValue={user.bio || ''}
                      className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={saving}
                    className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700 disabled:opacity-50"
                  >
                    {saving ? 'Saving...' : 'Save Changes'}
                  </button>
                </div>
              </form>
            </div>
          )}

          {activeTab === 'permissions' && (
            <div className="p-6">
              <h2 className="text-xl font-semibold mb-4">🔐 Manage Permissions</h2>
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-medium mb-3">Change User Role</h3>
                  <div className="flex items-center gap-4">
                    <select
                      value={user.role || 'host'}
                      onChange={(e) => handleChangeRole(e.target.value)}
                      disabled={saving}
                      className="bg-gray-100 border border-gray-300 rounded px-3 py-2"
                    >
                      <option value="host">Host</option>
                      <option value="team_captain">Team Captain</option>
                      <option value="team_member">Team Member</option>
                      <option value="venue">Venue</option>
                      <option value="admin">Admin</option>
                    </select>
                    {saving && <span className="text-gray-500">Updating...</span>}
                  </div>
                </div>

                {/* Email Verification Actions - Show for ALL users */}
                <div>
                  <h3 className="text-lg font-medium mb-3">Email Verification</h3>
                  <div className="space-y-3">
                    <button
                      onClick={() => handleUpdateUser({ emailVerified: true, emailVerifiedAt: new Date() })}
                      disabled={saving || user.emailVerified}
                      className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700 disabled:opacity-50"
                    >
                      ✅ Verify Email (Database)
                    </button>
                    {/* SECURITY: Removed dangerous bypass button */}
                    <button
                      onClick={handleResendVerificationEmail}
                      disabled={saving}
                      className="bg-yellow-600 text-white px-4 py-2 rounded hover:bg-yellow-700 disabled:opacity-50"
                    >
                      📧 Resend Verification Email
                    </button>
                    <button
                      onClick={handleResetPassword}
                      disabled={saving}
                      className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 disabled:opacity-50"
                    >
                      🔑 Reset Password
                    </button>
                  </div>
                </div>

                {/* Only show Make Admin and Credit Management if not a team */}
                {!isTeam && (
                  <>
                    <div>
                      <h3 className="text-lg font-medium mb-3">Quick Actions</h3>
                      <div className="space-y-3">
                        {user.role !== 'admin' && (
                          <button
                            onClick={handleMakeAdmin}
                            disabled={saving}
                            className="bg-purple-600 text-white px-4 py-2 rounded hover:bg-purple-700 disabled:opacity-50"
                          >
                            👑 Make Admin
                          </button>
                        )}
                      </div>
                    </div>

                    <div>
                      <h3 className="text-lg font-medium mb-3">🎁 Grant Free Access</h3>
                      <p className="text-gray-600 text-sm mb-4">
                        Grant free access to games, launch rights, or creator privileges for customer service
                      </p>
                      
                      {/* Grant Type Selection */}
                      <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Grant Type
                        </label>
                        <div className="grid grid-cols-3 gap-2">
                          <button
                            type="button"
                            onClick={() => setGrantType('launch')}
                            className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                              grantType === 'launch'
                                ? 'bg-blue-600 text-white'
                                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                            }`}
                          >
                            🚀 Launch Rights
                          </button>
                          <button
                            type="button"
                            onClick={() => setGrantType('game')}
                            className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                              grantType === 'game'
                                ? 'bg-green-600 text-white'
                                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                            }`}
                          >
                            🎮 Game Access
                          </button>
                          <button
                            type="button"
                            onClick={() => setGrantType('creator')}
                            className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                              grantType === 'creator'
                                ? 'bg-purple-600 text-white'
                                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                            }`}
                          >
                            👑 Creator Access
                          </button>
                        </div>
                      </div>

                                 {/* Game Selection (for launch and game grants) */}
           {grantType !== 'creator' && (
             <div className="mb-4">
               <label className="block text-sm font-medium text-gray-700 mb-2">
                 Select Game
               </label>
               {games.length === 0 ? (
                 <div className="text-sm text-gray-500 p-2 border border-gray-300 rounded bg-gray-50">
                   {grantMessage && grantMessage.includes('No games found') ? 
                     grantMessage : 
                     'Loading games...'
                   }
                 </div>
               ) : (
                 <div className="space-y-2">
                   <input
                     type="text"
                     placeholder="Search games..."
                     className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                     onChange={(e) => {
                       const searchTerm = e.target.value.toLowerCase();
                       const filteredGames = games.filter(game => 
                         game.title?.toLowerCase().includes(searchTerm) ||
                         game.description?.toLowerCase().includes(searchTerm)
                       );
                       setFilteredGames(filteredGames);
                     }}
                   />
                   <select
                     value={selectedGame}
                     onChange={(e) => setSelectedGame(e.target.value)}
                     className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                     size="5"
                   >
                     <option value="">Choose a game...</option>
                     {(filteredGames.length > 0 ? filteredGames : games).map((game) => (
                       <option key={game.id} value={game.id}>
                         {game.title} {game.status && `(${game.status})`}
                       </option>
                     ))}
                   </select>
                   <div className="text-xs text-gray-500">
                     Showing {filteredGames.length > 0 ? filteredGames.length : games.length} of {games.length} games
                   </div>
                 </div>
               )}
             </div>
           )}

                      {/* Duration Selection (for creator grants) */}
                      {grantType === 'creator' && (
                        <div className="mb-4">
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Duration (hours)
                          </label>
                          <input
                            type="number"
                            value={durationHours}
                            onChange={(e) => setDurationHours(parseInt(e.target.value) || 24)}
                            min="1"
                            max="168"
                            className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          />
                          <p className="text-xs text-gray-500 mt-1">
                            Maximum 168 hours (7 days)
                          </p>
                        </div>
                      )}

                      {/* Reason */}
                      <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Reason (optional)
                        </label>
                        <input
                          type="text"
                          value={reason}
                          onChange={(e) => setReason(e.target.value)}
                          placeholder="e.g., Customer service issue, technical problem"
                          className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                      </div>

                      {/* Grant Button */}
                      <button
                        onClick={handleGrantAccess}
                        disabled={grantLoading || (grantType !== 'creator' && !selectedGame)}
                        className={`w-full py-2 px-4 rounded-lg font-medium transition-colors ${
                          grantLoading || (grantType !== 'creator' && !selectedGame)
                            ? 'bg-gray-400 text-gray-600 cursor-not-allowed'
                            : 'bg-green-600 hover:bg-green-700 text-white'
                        }`}
                      >
                        {grantLoading ? 'Granting...' : `Grant ${grantType === 'launch' ? 'Launch Rights' : grantType === 'game' ? 'Game Access' : 'Creator Access'}`}
                      </button>

                      {/* Message */}
                      {grantMessage && (
                        <div className={`mt-4 p-3 rounded-lg text-sm ${
                          grantMessage.startsWith('✅') 
                            ? 'bg-green-100 border border-green-400 text-green-700'
                            : 'bg-red-100 border border-red-400 text-red-700'
                        }`}>
                          {grantMessage}
                        </div>
                      )}

                      {/* Current User Access */}
                      {userAccess && (
                        <div className="mt-6 pt-4 border-t border-gray-200">
                          <h4 className="text-sm font-medium text-gray-700 mb-3">Current Access</h4>
                          
                          {/* Launch Entitlements */}
                          {userAccess.launchEntitlements && userAccess.launchEntitlements.length > 0 && (
                            <div className="mb-3">
                              <h5 className="text-xs font-medium text-blue-600 mb-2">🚀 Launch Entitlements</h5>
                              <div className="space-y-1">
                                {userAccess.launchEntitlements.map((entitlement, index) => (
                                  <div key={index} className="text-xs text-gray-600 bg-gray-50 p-2 rounded">
                                    <div className="font-medium">{entitlement.gameTitle}</div>
                                    <div>Expires: {entitlement.expiresAt?.toLocaleString()}</div>
                                    <div>Source: {entitlement.source}</div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}

                          {/* Purchased Games */}
                          {userAccess.purchasedGames && userAccess.purchasedGames.length > 0 && (
                            <div className="mb-3">
                              <h5 className="text-xs font-medium text-green-600 mb-2">🎮 Purchased Games</h5>
                              <div className="space-y-1">
                                {userAccess.purchasedGames.map((game, index) => (
                                  <div key={index} className="text-xs text-gray-600 bg-gray-50 p-2 rounded">
                                    <div className="font-medium">{game.gameTitle}</div>
                                    <div>Purchased: {game.purchasedAt?.toLocaleString()}</div>
                                    <div>Source: {game.source}</div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}

                          {/* Creator Subscription */}
                          {userAccess.creatorSubscription && (
                            <div>
                              <h5 className="text-xs font-medium text-purple-600 mb-2">👑 Creator Subscription</h5>
                              <div className="text-xs text-gray-600 bg-gray-50 p-2 rounded">
                                <div>Status: {userAccess.creatorSubscription.active ? 'Active' : 'Inactive'}</div>
                                {userAccess.creatorSubscription.periodEnd && (
                                  <div>Expires: {new Date(userAccess.creatorSubscription.periodEnd).toLocaleString()}</div>
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Badge Management */}
                    <div>
                      <h3 className="text-lg font-medium mb-3">🏆 Badge Management</h3>
                      <div className="space-y-4">
                        {/* Current Badges */}
                        <div>
                          <h4 className="text-md font-medium mb-2">Current Badges ({currentBadges.length})</h4>
                          {currentBadges.length > 0 ? (
                            <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                              {currentBadges.map((badgeId) => {
                                const badge = availableBadges.find(b => b.id === badgeId);
                                return badge ? (
                                  <div key={badgeId} className="flex items-center justify-between p-2 bg-gray-100 rounded border">
                                    <div className="flex items-center space-x-2">
                                      <span className="text-lg">{badge.icon}</span>
                                      <span className="text-sm font-medium">{badge.name}</span>
                                    </div>
                                    <button
                                      onClick={() => handleRemoveBadge(badgeId)}
                                      disabled={badgeLoading}
                                      className="text-red-600 hover:text-red-800 text-sm font-medium"
                                    >
                                      Remove
                                    </button>
                                  </div>
                                ) : (
                                  <div key={badgeId} className="p-2 bg-gray-100 rounded border text-sm">
                                    {badgeId}
                                  </div>
                                );
                              })}
                            </div>
                          ) : (
                            <p className="text-gray-500 text-sm">No badges awarded yet.</p>
                          )}
                        </div>

                        {/* Award New Badge */}
                        <div>
                          <h4 className="text-md font-medium mb-2">Award New Badge</h4>
                          <div className="flex items-center gap-3">
                            <select
                              value={selectedBadge}
                              onChange={(e) => setSelectedBadge(e.target.value)}
                              disabled={badgeLoading}
                              className="flex-1 p-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            >
                              <option value="">Select a badge...</option>
                              {availableBadges
                                .filter(badge => !currentBadges.includes(badge.id))
                                .map((badge) => (
                                  <option key={badge.id} value={badge.id}>
                                    {badge.icon} {badge.name} ({badge.category})
                                  </option>
                                ))}
                            </select>
                            <button
                              onClick={handleAwardBadge}
                              disabled={badgeLoading || !selectedBadge}
                              className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700 disabled:opacity-50"
                            >
                              {badgeLoading ? 'Awarding...' : 'Award Badge'}
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </>
                )}

                {/* Subscription Management */}
                <div>
                  <h3 className="text-lg font-medium mb-3">💳 Subscription Management</h3>
                  <div className="space-y-4">
                    {/* Current Subscription Status */}
                    <div className="bg-gray-50 p-4 rounded">
                      <h4 className="text-md font-medium mb-2">Current Status</h4>
                      {user?.subscription ? (
                        <div className="space-y-2">
                          <div className="flex justify-between">
                            <span className="text-sm text-gray-600">Plan:</span>
                            <span className="text-sm font-medium">
                              {user.subscription.productId === 'prod_Ssvv7A01Jdt4uB' ? 'Creator Lite' :
                               user.subscription.productId === 'prod_SsvxGWP8OXQ96P' ? 'Creator Pro' :
                               'Unknown Plan'}
                            </span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-sm text-gray-600">Status:</span>
                            <span className={`text-sm font-medium ${
                              user.subscription.status === 'active' ? 'text-green-600' : 'text-red-600'
                            }`}>
                              {user.subscription.status}
                            </span>
                          </div>
                          {user.subscription.currentPeriodEnd && (
                            <div className="flex justify-between">
                              <span className="text-sm text-gray-600">Next Billing:</span>
                              <span className="text-sm font-medium">
                                {new Date(user.subscription.currentPeriodEnd).toLocaleDateString()}
                              </span>
                            </div>
                          )}
                        </div>
                      ) : (
                        <p className="text-sm text-gray-500">No active subscription</p>
                      )}
                    </div>

                    {/* Manual Role Assignment */}
                    <div className="bg-gray-50 p-4 rounded">
                      <h4 className="text-md font-medium mb-2">Manual Role Assignment</h4>
                      <div className="space-y-3">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Assign Creator Role
                          </label>
                          <div className="flex items-center gap-3">
                            <select
                              value={selectedCreatorRole || ''}
                              onChange={(e) => setSelectedCreatorRole(e.target.value)}
                              className="flex-1 p-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            >
                              <option value="">Select role...</option>
                              <option value="creator_lite">Creator Lite</option>
                              <option value="creator_pro">Creator Pro</option>
                              <option value="host">Regular Host</option>
                            </select>
                            <select
                              value={selectedDuration || ''}
                              onChange={(e) => setSelectedDuration(e.target.value)}
                              className="flex-1 p-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            >
                              <option value="">Select duration...</option>
                              <option value="1_week">1 Week</option>
                              <option value="1_month">1 Month</option>
                              <option value="3_months">3 Months</option>
                              <option value="6_months">6 Months</option>
                              <option value="1_year">1 Year</option>
                              <option value="permanent">Permanent</option>
                            </select>
                            <button
                              onClick={handleAssignCreatorRole}
                              disabled={!selectedCreatorRole || !selectedDuration || saving}
                              className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 disabled:opacity-50"
                            >
                              {saving ? 'Assigning...' : 'Assign Role'}
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Entitlements Reset */}
                    <div className="bg-gray-50 p-4 rounded">
                      <h4 className="text-md font-medium mb-2">Monthly Entitlements</h4>
                      {user?.creatorEntitlements ? (
                        <div className="space-y-2">
                          <div className="flex justify-between">
                            <span className="text-sm text-gray-600">Builds Used:</span>
                            <span className="text-sm font-medium">
                              {user.creatorEntitlements.buildsThisPeriod || 0}
                            </span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-sm text-gray-600">Submissions Used:</span>
                            <span className="text-sm font-medium">
                              {user.creatorEntitlements.submitsThisPeriod || 0}
                            </span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-sm text-gray-600">Reset Date:</span>
                            <span className="text-sm font-medium">
                              {user.creatorEntitlements.periodEnd ? 
                                new Date(user.creatorEntitlements.periodEnd).toLocaleDateString() : 
                                'Not set'}
                            </span>
                          </div>
                          <button
                            onClick={handleResetEntitlements}
                            disabled={saving}
                            className="bg-yellow-600 text-white px-4 py-2 rounded hover:bg-yellow-700 disabled:opacity-50 text-sm"
                          >
                            {saving ? 'Resetting...' : 'Reset Entitlements'}
                          </button>
                        </div>
                      ) : (
                        <p className="text-sm text-gray-500">No entitlements data</p>
                      )}
                    </div>
                  </div>
                </div>

                {/* Permissions summary always shown */}
                <div>
                  <h3 className="text-lg font-medium mb-3">Current Permissions</h3>
                  <div className="bg-gray-50 p-4 rounded">
                    <ul className="space-y-2 text-sm">
                      <li>• Role: {user.role || 'host'}</li>
                      
                      {/* Show League ID for team users */}
                      {isTeam && user.leagueId && (
                        <li>• League ID: <span className="font-mono">#{user.leagueId}</span> 
                          {user.leagueId === '1' && ' 🏆 First Team Ever!'}
                        </li>
                      )}
                      {/* Show Host ID for host users */}
                      {user.role === 'host' && user.hostId && (
                        <li>• Host ID: <span className="font-mono">#{user.hostId}</span> 
                          {user.hostId === '1' && ' 🎤 First Host Ever!'}
                        </li>
                      )}
                      <li>• Email Verified: {user.emailVerified ? '✅ Yes' : '❌ No'}</li>
                      <li>• Deleted: {user.deleted ? 'Yes' : 'No'}</li>
                      <li>• Admin Access: {user.role === 'admin' ? 'Yes' : 'No'}</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'activity' && (
            <div className="p-6">
              <h2 className="text-xl font-semibold mb-4">📊 User Activity</h2>
              <div className="space-y-4">
                <div className="bg-gray-50 p-4 rounded">
                  <h3 className="font-medium mb-2">Account Statistics</h3>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-gray-600">Member since:</span>
                      <p className="font-medium">{user.createdAt?.toDate?.().toLocaleDateString?.() || 'Unknown'}</p>
                    </div>
                    <div>
                      <span className="text-gray-600">Last active:</span>
                      <p className="font-medium">{user.lastActive?.toDate?.().toLocaleDateString?.() || 'Unknown'}</p>
                    </div>
                    {/* Show League ID for team users */}
                    {isTeam && user.leagueId && (
                      <>
                        <div>
                          <span className="text-gray-600">League ID:</span>
                          <p className="font-medium font-mono">#{user.leagueId}</p>
                        </div>
                        <div>
                          <span className="text-gray-600">League Status:</span>
                          <p className="font-medium">
                            {user.leagueId === '1' ? '🏆 First Team Ever!' : 
                             user.leagueId <= '10' ? '🥇 Early Adopter' :
                             user.leagueId <= '50' ? '🥈 Founding Member' :
                             user.leagueId <= '100' ? '🥉 Early Member' : 'Member'}
                          </p>
                        </div>
                      </>
                    )}
                    {/* Show Host ID for host users */}
                    {user.role === 'host' && user.hostId && (
                      <>
                        <div>
                          <span className="text-gray-600">Host ID:</span>
                          <p className="font-medium font-mono">#{user.hostId}</p>
                        </div>
                        <div>
                          <span className="text-gray-600">Host Status:</span>
                          <p className="font-medium">
                            {user.hostId === '1' ? '🎤 First Host Ever!' : 
                             user.hostId <= '10' ? '🥇 Early Adopter' :
                             user.hostId <= '50' ? '🥈 Founding Host' :
                             user.hostId <= '100' ? '🥉 Early Host' : 'Host'}
                          </p>
                        </div>
                      </>
                    )}
                  </div>
                </div>

                <div className="bg-gray-50 p-4 rounded">
                  <h3 className="font-medium mb-2">Recent Activity</h3>
                  <p className="text-gray-600 text-sm">Activity tracking will be implemented in future updates.</p>
                </div>

                <div className="bg-gray-50 p-4 rounded">
                  <h3 className="font-medium mb-2">Account Status</h3>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span>Account Status:</span>
                      <span className={user.deleted ? 'text-red-600' : 'text-green-600'}>
                        {user.deleted ? 'Deleted' : 'Active'}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span>Email Verified:</span>
                      <span className={user.emailVerified ? 'text-green-600' : 'text-red-600'}>
                        {user.emailVerified ? 'Yes' : 'No'}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span>Account Verified:</span>
                      <span className={user.verified ? 'text-green-600' : 'text-red-600'}>
                        {user.verified ? 'Yes' : 'No'}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'danger' && (
            <div className="p-6">
              <h2 className="text-xl font-semibold mb-4 text-red-600">⚠️ Danger Zone</h2>
              <div className="space-y-6">
                <div className="border border-red-200 bg-red-50 p-4 rounded">
                  <h3 className="text-lg font-medium text-red-800 mb-2">Delete User</h3>
                  <p className="text-red-700 text-sm mb-3">
                    This will mark the user as deleted and remove their access to the system. 
                    Their data will be preserved for audit purposes.
                  </p>
                  <button
                    onClick={handleDeleteUser}
                    disabled={saving}
                    className="bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700 disabled:opacity-50"
                  >
                    🗑️ Mark as Deleted
                  </button>
                </div>

                <div className="border border-red-200 bg-red-50 p-4 rounded">
                  <h3 className="text-lg font-medium text-red-800 mb-2">Permanently Delete User</h3>
                  <p className="text-red-700 text-sm mb-3">
                    <strong>WARNING:</strong> This action is irreversible and will completely remove all user data 
                    from the database including profile, game history, team memberships, and all associated information.
                  </p>
                  <button
                    onClick={handlePermanentlyDelete}
                    disabled={saving}
                    className="bg-red-800 text-white px-4 py-2 rounded hover:bg-red-900 disabled:opacity-50"
                  >
                    🚨 Permanently Delete
                  </button>
                </div>

                <div className="border border-yellow-200 bg-yellow-50 p-4 rounded">
                  <h3 className="text-lg font-medium text-yellow-800 mb-2">Reset User Data</h3>
                  <p className="text-yellow-700 text-sm mb-3">
                    Reset user's credits, preferences, and other data to default values.
                  </p>
                  <button
                    onClick={() => {
                      if (window.confirm('Reset user data to defaults?')) {
                        handleUpdateUser({
                  
                          preferences: {},
                          resetAt: new Date()
                        });
                      }
                    }}
                    disabled={saving}
                    className="bg-yellow-600 text-white px-4 py-2 rounded hover:bg-yellow-700 disabled:opacity-50"
                  >
                    🔄 Reset Data
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminUserDetail; 